import { useEffect, useState } from "react";

/**
 * useOnScreen: triggers once when an element first appears in the viewport.
 * Then remains true for the lifetime of the page (resets on page refresh).
 *
 * @param {React.RefObject} ref        - the ref of the element to observe
 * @param {string} rootMargin         - optional margin around viewport
 * @returns {boolean} isIntersecting  - true once it's on screen the first time
 */
export default function useOnScreen(ref, rootMargin = "0px") {
	const [isIntersecting, setIntersecting] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				// If we have NOT yet set it to true, and the entry is in view
				if (!isIntersecting && entry.isIntersecting) {
					setIntersecting(true);
					// Unobserve so it doesn't flip back to false
					observer.unobserve(entry.target);
				}
			},
			{ rootMargin }
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		// Cleanup on unmount
		return () => {
			observer.disconnect();
		};
	}, [ref, rootMargin, isIntersecting]);

	return isIntersecting;
}
